import { createPath } from 'history';
import { parse } from 'qs';
import { createElement, FC, Fragment, MouseEvent, useContext, useState } from 'react';
import { Redirect, RouteComponentProps } from 'react-router';

import ProjectOrderTarget from '../../entity/ProjectOrderTarget';
import { OrganizationDetailFragment } from '../../generated/graphql';
import mergeUrlQuery from '../../utility/mergeUrlQuery';
import DraftProjectDeleteDialog from '../component/compound/DraftProjectDeleteDialog';
import TransitionToKaizenIdModal from '../component/compound/TransitionToKaizenIdModal';
import { context as ConfigContext } from '../context/config';
import { context as DependencyContext } from '../context/dependency';
import { context as HistoryContext } from '../context/history';
import { context as StateContext } from '../context/state';
import Page from '../page/ProjectsListPage';
import TrialPage from '../page/ProjectsListPage/TrialProjectsListPage';

type Props = RouteComponentProps<Record<string, string | undefined>>;

const ProjectsListPage: FC<Props> = () => {
  const dependency = useContext(DependencyContext);
  const history = useContext(HistoryContext);
  const config = useContext(ConfigContext);
  const state = useContext(StateContext);

  const [projectToDelete, setDeleteDialog] = useState<{ id: string } | null>(null);
  const [isModalOpen, setModal] = useState(false);

  const query = parse(state.location.search.slice(1));
  const organizationId = query.org;

  if (!organizationId) {
    let organization!: OrganizationDetailFragment;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else if (state.availableOrganizations.items.length >= 1) {
      organization = state.availableOrganizations.items[0];
    }
    if (organization) {
      return <Redirect to={createPath(mergeUrlQuery(history.location, { org: organization.id }))} />;
    }
  } else {
    if (!state.availableOrganizations.items.some((item) => item.id === organizationId)) {
      return <Redirect to="/projects" />;
    }
  }

  // 新規プロジェクト作成時のRegion別処理を実行
  const handleClickNewProjectButton = () => {
    const organization = state.lastAccessedOrganization;
    const user = state.authentication.signedInUser;

    if (organization) {
      const region = organization.businessRegion.key;

      switch (region) {
        case 'jp':
          history.push(`/direction_types`);
          break;
        case 'us':
          history.push(`/direction_types`);
          break;
        case 'gl':
          history.push(`/direction_types`);
          break;
        default:
          break;
      }
    } else if (user) {
      // Organizationが存在しない場合、モーダルを表示してIDへの組織登録を促す
      // Organization.BusinessRegionを参照できないため、User.Langを参照する
      // https://kaizen.qiita.com/yuta-yamamoto/items/7d1a72e3d01ec3c5ec8f

      const language = state.language;

      switch (language) {
        case 'ja-JP':
          history.push(`/direction_types`);
          break;
        case 'en-US':
          setModal(true);
          break;
        default:
          break;
      }
    }
  };

  if (!organizationId) {
    return (
      <>
        <TrialPage
          onClickNewProjectButton={handleClickNewProjectButton}
          onMount={async () => {
            dependency.authenticationUsecase.needSignedIn();
          }}
        />
        {state.authentication.signedInUser ? (
          <TransitionToKaizenIdModal
            urlToId={config.ID_WEB_URL}
            open={isModalOpen}
            language={state.language}
            onRequestClose={() => setModal(false)}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <Page
        organizationId={organizationId}
        notDraftProjects={state.projects.notDraft.items}
        draftProjects={state.projects.draft.items}
        finalizedProjects={state.projects.finalized.items}
        hasDraftProjects={state.projects.notDraft.hasDraftItems}
        notDraftProjectsLoading={state.projects.notDraft.fetching >= 1}
        draftProjectsLoading={state.projects.draft.fetching >= 1}
        finalizedProjectsLoading={state.projects.finalized.fetching >= 1}
        hasMoreNotDraftProjects={
          state.projects.notDraft.pageInfo ? state.projects.notDraft.pageInfo.hasNextPage : false
        }
        hasMoreDraftProjects={state.projects.draft.pageInfo ? state.projects.draft.pageInfo.hasNextPage : false}
        hasMoreFinalizedProjects={
          state.projects.finalized.pageInfo ? state.projects.finalized.pageInfo.hasNextPage : false
        }
        getOrderedProjectList={(projectOrderTarget: ProjectOrderTarget) => {
          dependency.listProjectUsecase.getProjectsSortedInNewOrder(organizationId, projectOrderTarget);
        }}
        onClickLoadMoreNotDraftProjectButton={(projectOrderTarget: ProjectOrderTarget) => {
          if (state.projects.notDraft.pageInfo && state.projects.notDraft.pageInfo.endCursor) {
            dependency.listProjectUsecase.loadMoreProjects(
              organizationId,
              'notDraft',
              projectOrderTarget,
              state.projects.notDraft.pageInfo.endCursor,
            );
          }
        }}
        onClickLoadMoreDraftProjectButton={() => {
          if (state.projects.draft.pageInfo && state.projects.draft.pageInfo.endCursor) {
            dependency.listProjectUsecase.loadMoreProjects(
              organizationId,
              'draft',
              'updatedAt',
              state.projects.draft.pageInfo.endCursor,
            );
          }
        }}
        onClickLoadMoreFinalizedProjectButton={() => {
          if (state.projects.finalized.pageInfo && state.projects.finalized.pageInfo.endCursor) {
            dependency.listProjectUsecase.loadMoreProjects(
              organizationId,
              'finalized',
              'updatedAt',
              state.projects.finalized.pageInfo.endCursor,
            );
          }
        }}
        onClickNewProjectButton={handleClickNewProjectButton}
        onClickAnyDeleteButton={(e: MouseEvent<HTMLElement>, project) => {
          e.preventDefault();
          e.stopPropagation();
          setDeleteDialog(project);
        }}
        onChangeOrganization={(organizationId: string) => {
          dependency.listProjectUsecase.disposeProjects();
          dependency.listProjectUsecase.prepareProjects(organizationId);
        }}
        onMount={async () => {
          dependency.authenticationUsecase.needSignedIn();
          await dependency.listProjectUsecase.prepareProjects(organizationId);
        }}
        onUnmount={() => dependency.listProjectUsecase.disposeProjects()}
      />

      <DraftProjectDeleteDialog
        onClickAcceptButton={() => {
          setDeleteDialog(null);
          dependency.deleteProjectUsecase.deleteDraftProject(projectToDelete!);
        }}
        onRequestClose={() => setDeleteDialog(null)}
        open={!!projectToDelete}
      />

      {state.authentication.signedInUser ? (
        <TransitionToKaizenIdModal
          urlToId={config.ID_WEB_URL}
          open={isModalOpen}
          language={state.language}
          onRequestClose={() => setModal(false)}
        />
      ) : null}
    </>
  );
};

export default ProjectsListPage;
